import { LATENCY_SOURCE, LatencySource } from '../networkLatencyObserver';

import {
  MIN_THROUGHPUT_SAMPLE_BYTES,
  SERVER_TIMING_PROCESSING_METRICS,
} from './resourceTiming.constants';

export interface NetworkTimingBreakdown {
  networkLatencyMs?: number;
  latencySource?: LatencySource;
  ttfbMs?: number;
  connectionMs?: number;
  serverProcessingMs?: number;
  transferMs?: number;
  throughputMbps?: number;
}

/**
 * Detailed timestamps (`domainLookup*`, `connect*`, `requestStart`,
 * `responseStart`) are zeroed by the browser for cross-origin resources unless
 * the response carries a `Timing-Allow-Origin` header. When that happens we
 * cannot isolate the network phase, so we treat the entry as opaque.
 */
const hasDetailedTiming = (entry: PerformanceResourceTiming): boolean => {
  return entry.requestStart > 0 && entry.responseStart > 0;
};

const readServerProcessingMs = (
  entry: PerformanceResourceTiming,
): number | undefined => {
  const serverTiming = entry.serverTiming;

  if (!serverTiming || serverTiming.length === 0) {
    return undefined;
  }

  const matchedMetric = SERVER_TIMING_PROCESSING_METRICS.reduce<
    number | undefined
  >((found, metricName) => {
    if (found !== undefined) {
      return found;
    }

    const metric = serverTiming.find(
      (entryMetric) => entryMetric.name.toLowerCase() === metricName,
    );

    if (metric && metric.duration > 0) {
      return metric.duration;
    }

    return undefined;
  }, undefined);

  return matchedMetric;
};

/**
 * Throughput of the response body transfer, in Mbps. Backend-independent: the
 * transfer phase happens after `responseStart`, i.e. once the backend has
 * finished. Only computed for payloads large enough to be bandwidth-bound
 * rather than latency-bound.
 */
const computeThroughputMbps = (
  entry: PerformanceResourceTiming,
  transferMs: number | undefined,
): number | undefined => {
  if (transferMs === undefined || transferMs <= 0) {
    return undefined;
  }

  const bytes = entry.encodedBodySize || entry.transferSize;

  if (!bytes || bytes < MIN_THROUGHPUT_SAMPLE_BYTES) {
    return undefined;
  }

  return (bytes * 8) / (transferMs / 1000) / 1_000_000;
};

/**
 * Derives the pure network latency from a resource timing entry, independent of
 * the backend processing time. Cascade, most-to-least accurate:
 *  1. `Server-Timing` → `TTFB − backendProcessing`
 *  2. connection handshake (`connectEnd − connectStart`) → pure RTT, only set
 *     when a fresh connection was opened (0 on reused keep-alive connections)
 * When neither can isolate the network, `networkLatencyMs` is left undefined so
 * the consumer falls back to the legacy round-trip duration.
 */
export const extractNetworkLatency = (
  entry: PerformanceResourceTiming,
): NetworkTimingBreakdown => {
  if (!hasDetailedTiming(entry)) {
    return {};
  }

  const ttfbMs = entry.responseStart - entry.requestStart;
  const connectionMs =
    entry.connectEnd > entry.connectStart
      ? entry.connectEnd - entry.connectStart
      : undefined;
  const transferMs =
    entry.responseEnd > entry.responseStart
      ? entry.responseEnd - entry.responseStart
      : undefined;
  const throughputMbps = computeThroughputMbps(entry, transferMs);
  const serverProcessingMs = readServerProcessingMs(entry);

  const transferBreakdown = { ttfbMs, connectionMs, transferMs, throughputMbps };

  if (serverProcessingMs !== undefined) {
    return {
      ...transferBreakdown,
      networkLatencyMs: Math.max(0, ttfbMs - serverProcessingMs),
      latencySource: LATENCY_SOURCE.SERVER_TIMING,
      serverProcessingMs,
    };
  }

  if (connectionMs !== undefined && connectionMs > 0) {
    return {
      ...transferBreakdown,
      networkLatencyMs: connectionMs,
      latencySource: LATENCY_SOURCE.CONNECTION,
    };
  }

  return transferBreakdown;
};
